import { useEffect, useState } from "react";
import { GAME_SCREENS, NUMBER_CONSTANTS } from "./src/constants/constants";
import generatePseudorandomNumberBetweenMinAndMax from "./src/util/generatePseudorandomNumberBetweenMinAndMax";
import GameScreen from "./src/components/screens/GameScreen";
import HomeScreen from "./src/components/screens/HomeScreen";

import generateRandomQuestionChoices from "./src/util/generateRandomQuestionChoices";
import generateTotalIncorrectChoices from "./src/util/generateTotalIncorrectChoices";
import LearnMoreScreen from "./src/components/screens/LearnMoreScreen";
import CorrectScreen from "./src/components/screens/CorrectScreen";
import EndScreen from "./src/components/screens/EndScreen";
import StatsScreen from "./src/components/screens/StatsScreen";
import AccentCaptureScreen from "./src/components/screens/AccentCaptureScreen";
import { LOCAL_STORAGE_KEYS } from "./src/constants/constants";
import {
  addValueToArrayInStorage,
  addDataToCurrentValue,
  deleteData,
  readData,
  storeData
} from "./src/util/AsyncStorage/storeChoice";
import * as Updates from 'expo-updates'
import scoreRound from "./src/util/scoreRound";


type GameScreenStateSetter = React.Dispatch<React.SetStateAction<GAME_SCREENS>>;
type CurrentQuestionSetter = React.Dispatch<React.SetStateAction<number>>;

export default function App() {
  /* STATE VARIABLES START */
  let [difficultyLevel, setDifficultyLevel] = useState<number>(1)
  let [correctChoicesArray, setCorrectChoicesArray] = useState<any>([]);
  let [incorrectChoicesArray, setIncorrectChoicesArray] = useState<any>([]);
  let [currentGameIndex, setCurrentGameIndex] = useState(
    NUMBER_CONSTANTS.INDEX_OUT_OF_RANGE
  );
  let [gameScreen, setScreen] = useState<GAME_SCREENS>(GAME_SCREENS.HOMESCREEN);
  let [userSelectedChoicesRecord, setUserSelectedChoicesRecord] = useState<
    number[][]
  >([[]]);

  let [correctChoiceButtonIndex, setCorrectChoiceButtonIndex] =
    useState<number>(NUMBER_CONSTANTS.ZEROTH_INDEX);
  let [currentRoundScore, setCurrentRoundScore] = useState<number>(
    NUMBER_CONSTANTS.INIT_SCORE
  );
  let [dataLoaded, setDataLoaded] = useState<boolean>(false)
  let [data, setData] = useState<any>([])
  /* STATE VARIABLES END */

  /* EVENT HANDLERS START */
  const getData = async () =>{
    try {
      const response = await fetch('https://saltire.com/speech/all_metadata.php', {
        method: "GET",
        headers: {
          'Content-type': 'application/json'
        },
        })
        
        let accents = await response.json()
        accents = accents
        accents = accents.map((accent: any) => {
          const updatedObj = { ...accent };
          for (const prop in updatedObj) {
            if (updatedObj[prop] === null) {
              updatedObj[prop] = "";
            }
          }
          return updatedObj
        })

        accents.forEach((obj: any) => {
          if ('longtitude' in obj) {
            obj.longitude = obj.longtitude;
            delete obj.longtitude;
          }
        });
        accents = accents.filter((accent: any) =>
          (accent.tier1 !== "" && accent.tier2 !== "" && accent.tier3 !== "" &&accent.tier4 !== "")
        )
        

        
        setData(accents)
        setDataLoaded(true)
    }catch (e){
      console.error(e)
    }
  }

  useEffect(() => {
    getData()
  }, []);


  const handleGoToHome = () => {
    const screen = GAME_SCREENS.HOMESCREEN;
    setScreen(screen);
  };

  const handleNextQuestion = async () => {
    let newIndex: number;
    let screen: GAME_SCREENS;
    const lastIndexValue = correctChoicesArray.length - 1;

    if (currentGameIndex === lastIndexValue) {
      screen = GAME_SCREENS.ENDSCREEN;
      newIndex = NUMBER_CONSTANTS.INDEX_OUT_OF_RANGE;
  
      if (difficultyLevel != 5){
        const curRoundScore = scoreRound(userSelectedChoicesRecord, correctChoicesArray)
        if (curRoundScore == 10 ){
          setDifficultyLevel(difficultyLevel + 1)
          await addDataToCurrentValue(LOCAL_STORAGE_KEYS.DIFFICULTY, 1)
          await storeData(LOCAL_STORAGE_KEYS.ROUNDINCREASED, true)
        }
        else if (curRoundScore>=8){
          await addDataToCurrentValue(LOCAL_STORAGE_KEYS.consecutiveEightyPercentPassed, 1)
          const consecutivePassed = await readData(LOCAL_STORAGE_KEYS.consecutiveEightyPercentPassed)
          
          if (consecutivePassed === 2){
            await storeData(LOCAL_STORAGE_KEYS.consecutiveEightyPercentPassed, 0)     
            setDifficultyLevel(difficultyLevel + 1) 
            await addDataToCurrentValue(LOCAL_STORAGE_KEYS.DIFFICULTY, 1)
            await storeData(LOCAL_STORAGE_KEYS.ROUNDINCREASED, true)      
          }
          
        }else{
          await storeData(LOCAL_STORAGE_KEYS.consecutiveEightyPercentPassed, 0)     
        }
       
      }
      

      setScreen(screen);
      setCurrentGameIndex(newIndex);
    } else {
      let record = [...userSelectedChoicesRecord];
      record.push([]);
      newIndex = currentGameIndex + 1;
      screen = GAME_SCREENS.GAMESCREEN;
      setUserSelectedChoicesRecord(record);
      setCorrectChoiceButtonIndex(
        Math.floor(Math.random() * NUMBER_CONSTANTS.NUM_CHOICES_PER_QUESTION)
      );
    }

    setCurrentGameIndex(newIndex);
    setScreen(screen);
  };

  const handleLearnMore = () => {
    setScreen(GAME_SCREENS.LEARN_MORE);
  };

  const handleStartGameRound = async () => {
    await storeData(LOCAL_STORAGE_KEYS.ROUNDINCREASED, false)
    let currentDifficultyLevel = await readData(LOCAL_STORAGE_KEYS.DIFFICULTY)
    if (currentDifficultyLevel == ""){
      currentDifficultyLevel = 1
      await storeData(LOCAL_STORAGE_KEYS.DIFFICULTY, currentDifficultyLevel)
    }

    setDifficultyLevel(currentDifficultyLevel)
    try{
        let prevCorrectIds = await readData(LOCAL_STORAGE_KEYS.CORRECT_CHOICES)
        if (prevCorrectIds == ""){
          prevCorrectIds = []
        }
        
        let correctChoices: any = await generateRandomQuestionChoices(data, 10, difficultyLevel)
        let incorrectChoices: any = generateTotalIncorrectChoices(correctChoices, data, difficultyLevel)
        

        prevCorrectIds.push((correctChoices).map((accent: any) => accent.fileID))
        if (prevCorrectIds.length > 2){
          prevCorrectIds = prevCorrectIds.slice(1)
        }
        await storeData(LOCAL_STORAGE_KEYS.CORRECT_CHOICES, prevCorrectIds)

        setCurrentRoundScore(NUMBER_CONSTANTS.INIT_SCORE);
        setCorrectChoicesArray(correctChoices);
        setIncorrectChoicesArray(incorrectChoices);

        setUserSelectedChoicesRecord([[]]);
        setCurrentGameIndex(NUMBER_CONSTANTS.ZEROTH_INDEX);
        setCorrectChoiceButtonIndex(Math.floor(Math.random() * NUMBER_CONSTANTS.NUM_CHOICES_PER_QUESTION));
        setScreen(GAME_SCREENS.GAMESCREEN)


        
    }catch (err){
      console.error(err)
    }
  };

  const handleAnswerSelection = (id: number) => {
    let screen: GAME_SCREENS;
    let record = [...userSelectedChoicesRecord];
    record[currentGameIndex].push(id);
    setUserSelectedChoicesRecord(record);

    if (record[currentGameIndex].length == 1) {
     
    }

    if (id === correctChoicesArray[currentGameIndex].fileID) {
      if (record[currentGameIndex].length == 1) {
        const newCurrentScore: number = currentRoundScore + 1;
        setCurrentRoundScore(newCurrentScore);
        addDataToCurrentValue(LOCAL_STORAGE_KEYS.firstChoiceCorrectScoreKey, 1);
      }
      addDataToCurrentValue(LOCAL_STORAGE_KEYS.questionsPlayedKey, 1);
      screen = GAME_SCREENS.CORRECT;
      setScreen(screen);
    }
  };

  const handleGoToStats = () => {
    setScreen(GAME_SCREENS.STAT_SCREEN);
  };
  /* EVENT HANDLERS END */

  return (
    <>
      {gameScreen === GAME_SCREENS.HOMESCREEN && (
        <HomeScreen
          doOnStartGameRound={handleStartGameRound}
          handleGoToStats={handleGoToStats}
        />
      )}
      {gameScreen === GAME_SCREENS.GAMESCREEN &&
        correctChoicesArray.length > 0 && (
          <GameScreen
            allIncorrect={incorrectChoicesArray}
            currentQuestionIndex={currentGameIndex}
            handleAnswerSelection={handleAnswerSelection}
            correctChoiceObj={correctChoicesArray[currentGameIndex]}
            correctButtonIndex={correctChoiceButtonIndex}
            currentRoundScore={currentRoundScore}
            difficultyLevel={difficultyLevel}
          />
        )}
      {gameScreen === GAME_SCREENS.LEARN_MORE && (
        <LearnMoreScreen
          correctChoiceObj={correctChoicesArray[currentGameIndex]}
          handleButtonPress={handleNextQuestion}
        />
      )}
      {gameScreen === GAME_SCREENS.CORRECT && (
        <CorrectScreen
          correctChoiceObj={correctChoicesArray[currentGameIndex]}
          handleNextButtonPress={handleNextQuestion}
          handleLearnMoreButtonPress={handleLearnMore}
        />
      )}
      {gameScreen === GAME_SCREENS.ENDSCREEN && (
        <EndScreen
          handleButtonPress={handleGoToHome}
          selections={userSelectedChoicesRecord}
          correctChoices={correctChoicesArray}
          level = {difficultyLevel}
        />
      )}
      {gameScreen === GAME_SCREENS.ACCENT_ACQ && <AccentCaptureScreen />}
      {gameScreen === GAME_SCREENS.STAT_SCREEN && (
        <StatsScreen handleGoToHome={handleGoToHome} />
      )}
    </>
  );
}

type AccentList = Accent[];

class Accent {
  id: number;
  displayString: string;
  url: string;
  constructor(linkToAudio: string) {
    this.id = generatePseudorandomNumberBetweenMinAndMax();
    this.displayString = "Display string";
    this.url = linkToAudio;
  }
}

export { Accent, AccentList, GameScreenStateSetter, CurrentQuestionSetter };
